import React from 'react';
import { Colors } from 'works/legacy/lib/style-helper';
import { Space } from 'antd';
import { ApprovalChainActionButtons } from '@approvals/components/approval-chain-action-buttons';
import { capitalizeFirstLetter } from '@common/services';
import { getNumberPositionSufix } from '@approvals/services/approvals-service';
import {
  Icon,
  TimeWithTimeZone,
  Text,
  PostMessage,
  Gap,
} from '@common/components';
import { ApprovalChainApproverType } from '@approvals/graphql/fragments';
import { ApprovalChainStatusType } from './_approval-chain-status';
import { CommentInfoFragmentType } from '@common/graphql';

type Props = {
  actionUsers: ApprovalChainApproverType[];
  approvalChainStepId: string;
  currentApprovers: ApprovalChainApproverType[];
  displayActionButtonsInsideStep: boolean;
  level: number;
  status: ApprovalChainStatusType;
  rejectionComment: string;
  rejectionReason: string;
  updatedAt?: string;
  extraApprovalButton?: React.ReactNode;
  stepComment?: CommentInfoFragmentType;
};

export const ApprovalChainStepDescription = ({
  actionUsers,
  approvalChainStepId,
  currentApprovers,
  displayActionButtonsInsideStep,
  level,
  status,
  rejectionComment,
  rejectionReason,
  updatedAt,
  extraApprovalButton,
  stepComment,
}: Props) => {
  const formattedActionUsers = formatActionUsers(actionUsers, status);
  const formattedLevel = getNumberPositionSufix(level);
  const formattedRejectionReason = capitalizeFirstLetter(
    rejectionReason?.toLowerCase().replace(/_/g, ' ')
  );

  return (
    <Gap align="stretch" vertical>
      <Gap align="center">
        <div style={{ height: 34 }} />
        <Text weight="bold">{formattedLevel} Level</Text>
        <StatusTag status={status} />
      </Gap>
      {formattedActionUsers}
      {rejectionReason && (
        <>
          <Text weight="bold">Reason: </Text>
          <Text weight="light" size="sm" color="midnightLighter">
            {formattedRejectionReason}
          </Text>
        </>
      )}
      {rejectionComment && (
        <>
          <Text weight="bold">Comment: </Text>
          <Text weight="light" size="sm" color="midnightLighter">
            {rejectionComment}
          </Text>
        </>
      )}
      {stepComment ? (
        <PostMessage
          time={stepComment.createdAt}
          name={stepComment.author.name}
          message={stepComment.contentAsText}
        />
      ) : (
        updatedAt && (
          <div style={{ marginTop: -8 }}>
            <Text weight="light" size="sm" color="midnightLighter">
              <TimeWithTimeZone date={updatedAt} variant="long_format" />
            </Text>
          </div>
        )
      )}
      {displayActionButtonsInsideStep && (
        <div style={{ marginTop: 8 }}>
          <ApprovalChainActionButtons
            extraApprovalButton={extraApprovalButton}
            approvalChainStepId={approvalChainStepId}
            approveButtonText="Approve"
            currentApprovers={currentApprovers}
            rejectionDrawerTitle="Reject Staffing Request"
          />
        </div>
      )}
    </Gap>
  );
};

const StatusTag = ({ status }: { status: ApprovalChainStatusType }) => {
  if (status === 'Wait') return null;
  if (status === 'Pending') {
    return (
      <span
        style={{
          backgroundColor: Colors.sunDarkest,
          padding: '0px 12px',
          borderRadius: '999px',
        }}
      >
        <Text weight="medium" size="sm" color="white">
          {status}
        </Text>
      </span>
    );
  }

  return (
    <Text weight="light" size="sm" color="midnightLighter">
      {status}
    </Text>
  );
};

const formatActionUsers = (
  approvers: ApprovalChainApproverType[],
  status: string
) => {
  return (
    <Space size={8} direction="vertical">
      {approvers.map((approver, index) => (
        <Space key={index} size={4}>
          <div
            style={{
              height: 36,
              backgroundColor: Colors.steelLightest,
              border: `1px solid ${Colors.steelLighter}`,
              borderRadius: 4,
              padding: '6px 8px 4px 8px',
            }}
          >
            <Space size={4} align="end">
              <Text>{approver.worksMembership.name}</Text>
              {status === 'Approved' && (
                <Icon icon="check" kind="light" color="midnightLightest" />
              )}
            </Space>
          </div>
          {index !== approvers.length - 1 && (
            <Text color="midnightLighter" size="sm" weight="light">
              OR
            </Text>
          )}
        </Space>
      ))}
    </Space>
  );
};
